import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, MapPin, ArrowRight, Sparkles, CalendarPlus } from "lucide-react";
import { TIMETABLE, TIMETABLE_FILTERS, DAY_ORDER } from "@/data/timetable";
import { BookingModal } from "./BookingModal";
import { trackEvent } from "@/lib/analytics";

const CATEGORY_BADGE = {
  adult: { label: "Adults", className: "text-brand-yellow border-brand-yellow/40 bg-brand-yellow/10" },
  kids: { label: "Kids", className: "text-brand-purpleLight border-brand-purpleLight/40 bg-brand-purpleLight/10" },
  workshop: { label: "Workshop", className: "text-white border-white/30 bg-white/10" },
};

// Cinematic weekly timetable built in USDD's own visual language.
// Acuity stays behind the scenes — clicking any "Reserve" opens the
// themed BookingModal lightbox.
//
// Props:
//   variant: "full" (default) | "compact" — compact hides the filter pills
//            and the day header strip; useful for embedding on home/kids
//   defaultFilter: "all" | "adult" | "kids" | "workshop"
//   limit: cap number of rendered slots (compact previews)
//   showCta: true to render a "See full timetable" link at the bottom
export const WeeklyTimetable = ({
  variant = "full",
  defaultFilter = "all",
  limit,
  showCta = false,
  testId = "weekly-timetable",
}) => {
  const [filter, setFilter] = useState(defaultFilter);
  const [active, setActive] = useState(null); // active slot for the modal

  const filtered = useMemo(() => {
    const all = filter === "all" ? TIMETABLE : TIMETABLE.filter((s) => s.category === filter);
    const sorted = [...all].sort((a, b) => {
      const da = DAY_ORDER.indexOf(a.day);
      const db = DAY_ORDER.indexOf(b.day);
      if (da !== db) return da - db;
      return a.time.localeCompare(b.time);
    });
    return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
  }, [filter, limit]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((s) => {
      if (!map.has(s.day)) map.set(s.day, []);
      map.get(s.day).push(s);
    });
    // Preserve DAY_ORDER ordering of keys
    return DAY_ORDER.filter((d) => map.has(d)).map((d) => [d, map.get(d)]);
  }, [filtered]);

  const openReserve = (slot) => {
    trackEvent("timetable_reserve_click", {
      title: slot.title,
      day: slot.day,
      time: slot.time,
      category: slot.category,
    });
    // Specific event for the Acuity-driven booking pathway, easier for
    // GTM-side conversion tags than a generic "click" event.
    trackEvent("acuity_click", {
      title: slot.title,
      category: slot.category,
      location: slot.location,
    });
    setActive(slot);
  };

  return (
    <div data-testid={testId} className="relative">
      {/* Filters */}
      {variant === "full" && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {TIMETABLE_FILTERS.map((f) => {
            const isActive = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`timetable-filter-${f.id}`}
                className={`px-4 py-2 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-[0.25em] transition-all ${
                  isActive
                    ? "bg-brand-yellow text-black glow-yellow"
                    : "border border-white/15 bg-white/[0.03] text-zinc-300 hover:border-brand-yellow/50 hover:text-brand-yellow"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Day-grouped list */}
      <div className="rounded-3xl border border-white/10 overflow-hidden bg-gradient-to-b from-zinc-950/70 to-black">
        {grouped.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-400">
            No classes match this filter right now — check back soon or{" "}
            <Link to="/contact" className="text-brand-yellow underline">contact us</Link>.
          </div>
        ) : (
          grouped.map(([day, slots]) => (
            <div key={day} className="border-b border-white/10 last:border-b-0">
              {/* Day header */}
              <div className="flex items-center justify-between px-5 sm:px-7 py-3 bg-gradient-to-r from-brand-purpleDeep/30 via-transparent to-transparent">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-2xl sm:text-3xl text-white tracking-wide leading-none">{day}</span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    {slots.length} {slots.length === 1 ? "class" : "classes"}
                  </span>
                </div>
                <Sparkles size={14} className="text-brand-yellow/60" />
              </div>

              {/* Slots */}
              <ul>
                {slots.map((s, i) => {
                  const badge = CATEGORY_BADGE[s.category] || CATEGORY_BADGE.adult;
                  return (
                    <motion.li
                      key={`${day}-${i}-${s.title}`}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: i * 0.03 }}
                      data-testid={`timetable-slot-${day.toLowerCase()}-${i}`}
                      className="group grid grid-cols-12 gap-3 sm:gap-6 items-center px-5 sm:px-7 py-4 border-t border-white/[0.06] hover:bg-white/[0.03] transition-colors"
                    >
                      <div className="col-span-12 sm:col-span-3 flex items-center gap-2 text-zinc-200">
                        <Clock size={14} className="text-brand-yellow flex-shrink-0" />
                        <span className="font-mono text-sm tracking-tight tabular-nums">{s.time}</span>
                      </div>
                      <div className="col-span-12 sm:col-span-5">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`inline-block px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-[0.2em] ${badge.className}`}>
                            {badge.label}
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{s.style}</span>
                        </div>
                        <div className="font-display text-lg text-white tracking-wide leading-tight">{s.title}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">{s.level}</div>
                      </div>
                      <div className="col-span-7 sm:col-span-2 text-xs text-zinc-400 inline-flex items-center gap-1.5">
                        <MapPin size={12} className="text-brand-yellow/70" /> {s.location}
                      </div>
                      <div className="col-span-5 sm:col-span-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => openReserve(s)}
                          data-testid={`timetable-reserve-${day.toLowerCase()}-${i}`}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-yellow text-black font-black uppercase text-[10px] sm:text-[11px] tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.04] transition-all glow-yellow"
                        >
                          <CalendarPlus size={12} /> Reserve
                        </button>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>

      {showCta && (
        <div className="mt-6 flex items-center justify-between gap-3 text-sm">
          <span className="text-xs text-zinc-500 uppercase tracking-[0.25em]">
            Times shown UK · Term-time
          </span>
          <Link
            to="/schedule"
            data-testid="timetable-see-full"
            className="inline-flex items-center gap-2 text-brand-yellow font-bold uppercase text-[11px] tracking-[0.25em] hover:gap-3 transition-all"
          >
            See full schedule <ArrowRight size={14} />
          </Link>
        </div>
      )}

      <BookingModal
        open={!!active}
        onClose={() => setActive(null)}
        title={active?.title || "Reserve your spot"}
        subtitle={active ? `${active.day} · ${active.time} · ${active.location}` : null}
        appointmentType={active?.appointmentType}
        category={active?.category}
        bookingUrl={active?.bookingUrl}
      />
    </div>
  );
};
